import { useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Mail, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * BoardPacketDialog — one-click "email a PDF snapshot of the Finance
 * dashboard to every seated director" for the current scope + range.
 *
 * Also lets admins preview the PDF in a new tab before sending.
 */
export function BoardPacketDialog({ open, onOpenChange, scope, scopeLabel, months }) {
  const [note, setNote] = useState("");
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/board/packet", {
        property_id: scope, months, note: note || null, include_admins: includeAdmins,
      });
      if (data.recipients === 0) {
        toast.error("No board members to send to. Invite directors first.");
        return;
      }
      const sentCount = (data.sent || []).length;
      const failedCount = (data.failed || []).length;
      if (failedCount > 0) {
        toast.warning(`Packet sent to ${sentCount} of ${data.recipients} directors (${failedCount} failed).`);
      } else {
        toast.success(`Packet emailed to ${sentCount} director${sentCount === 1 ? "" : "s"}`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to send board packet");
    } finally {
      setBusy(false);
    }
  };

  const previewHref = () => {
    const url = `${API}/admin/board/packet/preview.pdf?property_id=${encodeURIComponent(scope)}&months=${months}`;
    // Cookie auth (`credentials: 'include'`) — no bearer token in JS.
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("preview failed"))))
      .then((blob) => {
        const dl = URL.createObjectURL(blob);
        window.open(dl, "_blank");
        setTimeout(() => URL.revokeObjectURL(dl), 60_000);
      })
      .catch(() => toast.error("Preview failed"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="board-packet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-indigo-400" /> Email board packet
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Generate a PDF snapshot of the finance dashboard (KPIs, budget vs actual, AR aging, cashflow with 3-mo forecast,
            top delinquents) and email it to every seated director for <b className="text-white">{scopeLabel}</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div>
            <Label htmlFor="packet-note" className="text-zinc-300">
              Personal note (optional)
            </Label>
            <Textarea
              id="packet-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context for your board — anything you'd want them to focus on this month."
              className="mt-1 bg-white/[0.04] border-white/10 min-h-[90px] text-sm"
              data-testid="packet-note-input"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-white/10 px-3 py-2">
            <div className="text-xs">
              <Label htmlFor="packet-cc" className="text-zinc-200 cursor-pointer">Also send a copy to fellow admins</Label>
              <div className="text-[11px] text-zinc-500">Useful if property managers want the same PDF for their records.</div>
            </div>
            <Switch id="packet-cc" checked={includeAdmins} onCheckedChange={setIncludeAdmins} data-testid="packet-include-admins" />
          </div>

          <button
            onClick={previewHref}
            className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1"
            data-testid="packet-preview-btn"
            type="button"
          >
            <FileText className="h-3 w-3" /> Preview PDF <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-white/10" data-testid="packet-cancel">
            Cancel
          </Button>
          <Button onClick={send} disabled={busy} className="bg-indigo-500 hover:bg-indigo-600" data-testid="packet-send">
            {busy ? "Sending…" : "Email packet now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BoardPacketDialog;
